/**
 * Copyright IBM Corp. 2020, 2025
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, defineProperty as _defineProperty, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { usePrefix, Button, Toggle, Link, IconButton } from '@carbon/react';
import { dateTimeFormat } from '@carbon/utilities';
import { Settings, ErrorFilled, CheckmarkFilled, WarningAltFilled, InformationSquareFilled, Close, ChevronDown } from '@carbon/react/icons';
import React__default, { useRef, useState, useEffect } from 'react';
import '../EmptyStates/EmptyState.js';
import '../EmptyStates/EmptyStateV2.js';
import '../EmptyStates/ErrorEmptyState/ErrorEmptyState.js';
import '../EmptyStates/NoDataEmptyState/NoDataEmptyState.js';
import '../EmptyStates/NoTagsEmptyState/NoTagsEmptyState.js';
import '../EmptyStates/NotFoundEmptyState/NotFoundEmptyState.js';
import { NotificationsEmptyState } from '../EmptyStates/NotificationsEmptyState/NotificationsEmptyState.js';
import '../EmptyStates/UnauthorizedEmptyState/UnauthorizedEmptyState.js';
import PropTypes from '../../_virtual/index.js';
import cx from 'classnames';
import { usePrefersReducedMotion } from '../../global/js/hooks/usePrefersReducedMotion.js';
import { getDevtoolsProps } from '../../global/js/utils/devtools.js';
import { prepareProps } from '../../global/js/utils/props-helper.js';
import { getSupportedLocale } from '../../global/js/utils/getSupportedLocale.js';
import { pkg } from '../../settings.js';
import { timeAgo } from './utils.js';
import { usePresence } from '../../global/js/hooks/usePresence.js';
import { useClickOutside } from '../../global/js/hooks/useClickOutside.js';

var _Close;
var _excluded = ["className", "data", "dateTimeLocale", "dateTimeStyle", "daysAgoText", "dismissAllLabel", "dismissSingleNotificationIconDescription", "doNotDisturbDefaultToggled", "doNotDisturbLabel", "emptyStateLabel", "hourAgoText", "hoursAgoText", "illustrationTheme", "minuteAgoText", "minutesAgoText", "monthAgoText", "monthsAgoText", "nowText", "onClickOutside", "onDismissAllNotifications", "onDismissSingleNotification", "onDoNotDisturbChange", "onSettingsClick", "onViewAllClick", "open", "previousLabel", "readLessLabel", "readMoreLabel", "secondsAgoText", "settingsIconDescription", "title", "todayLabel", "viewAllLabel", "yearAgoText", "yearsAgoText", "yesterdayAtText", "yesterdayLabel", "triggerButtonRef"];

// The block part of our conventional BEM class names (blockClass__E--M).
var componentName = 'NotificationsPanel';
var blockClass = "".concat(pkg.prefix, "--notifications-panel");
var DefaultLocale = 'en-US';
// Default values for props
var defaults = {
  dateTimeStyle: 'long',
  daysAgoText: function daysAgoText(value) {
    return "".concat(value, " days ago");
  },
  dismissAllLabel: 'Dismiss all',
  dismissSingleNotificationIconDescription: 'Dismiss',
  doNotDisturbLabel: 'Do not disturb',
  emptyStateLabel: 'You do not have any notifications',
  hourAgoText: function hourAgoText(value) {
    return "".concat(value, " hour ago");
  },
  hoursAgoText: function hoursAgoText(value) {
    return "".concat(value, " hours ago");
  },
  illustrationTheme: 'light',
  minuteAgoText: function minuteAgoText(value) {
    return "".concat(value, " minute ago");
  },
  minutesAgoText: function minutesAgoText(value) {
    return "".concat(value, " minutes ago");
  },
  monthAgoText: function monthAgoText(value) {
    return "".concat(value, " month ago");
  },
  monthsAgoText: function monthsAgoText(value) {
    return "".concat(value, " months ago");
  },
  nowText: 'Now',
  onDismissAllNotifications: function onDismissAllNotifications() {},
  onDismissSingleNotification: function onDismissSingleNotification() {},
  previousLabel: 'Previous',
  readLessLabel: 'Read less',
  readMoreLabel: 'Read more',
  secondsAgoText: function secondsAgoText(value) {
    return "".concat(value, " seconds ago");
  },
  settingsIconDescription: 'Settings',
  title: 'Notifications',
  todayLabel: 'Today',
  viewAllLabel: function viewAllLabel(value) {
    return "View all (".concat(value, ")");
  },
  yearAgoText: function yearAgoText(value) {
    return "".concat(value, " year ago");
  },
  yearsAgoText: function yearsAgoText(value) {
    return "".concat(value, " years ago");
  },
  yesterdayAtText: function yesterdayAtText(value) {
    return "Yesterday at ".concat(value);
  },
  yesterdayLabel: 'Yesterday'
};
/**
 * The `NotificationsPanel` sets expectations on the behavior for notifications,
 * allowing the user to view and interact with them all in one place.
 *
 * **To adopt the new localization:**
 *
 * **Step 1:** Provide a locale to the `dateTimeLocale` prop, such as "de" or "fr-CA".
 *
 * **Step 2:** Remove the now _deprecated_ props: `daysAgoText`,
 * `hourAgoText`, `hoursAgoText`, `minuteAgoText`, `minutesAgoText`,
 * `monthAgoText`, `monthsAgoText`, `nowText`, `secondsAgoText`,
 * `yearAgoText`, `yearsAgoText`, `yesterdayAtText`.
 *
 * If you do not provide a locale, the deprecated props will be applied instead.
 */
var NotificationsPanel = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    data = _ref.data,
    dateTimeLocale = _ref.dateTimeLocale,
    _ref$dateTimeStyle = _ref.dateTimeStyle,
    dateTimeStyle = _ref$dateTimeStyle === void 0 ? defaults.dateTimeStyle : _ref$dateTimeStyle,
    _ref$daysAgoText = _ref.daysAgoText,
    daysAgoText = _ref$daysAgoText === void 0 ? defaults.daysAgoText : _ref$daysAgoText,
    _ref$dismissAllLabel = _ref.dismissAllLabel,
    dismissAllLabel = _ref$dismissAllLabel === void 0 ? defaults.dismissAllLabel : _ref$dismissAllLabel,
    _ref$dismissSingleNot = _ref.dismissSingleNotificationIconDescription,
    dismissSingleNotificationIconDescription = _ref$dismissSingleNot === void 0 ? defaults.dismissSingleNotificationIconDescription : _ref$dismissSingleNot,
    doNotDisturbDefaultToggled = _ref.doNotDisturbDefaultToggled,
    _ref$doNotDisturbLabe = _ref.doNotDisturbLabel,
    doNotDisturbLabel = _ref$doNotDisturbLabe === void 0 ? defaults.doNotDisturbLabel : _ref$doNotDisturbLabe,
    _ref$emptyStateLabel = _ref.emptyStateLabel,
    emptyStateLabel = _ref$emptyStateLabel === void 0 ? defaults.emptyStateLabel : _ref$emptyStateLabel,
    _ref$hourAgoText = _ref.hourAgoText,
    hourAgoText = _ref$hourAgoText === void 0 ? defaults.hourAgoText : _ref$hourAgoText,
    _ref$hoursAgoText = _ref.hoursAgoText,
    hoursAgoText = _ref$hoursAgoText === void 0 ? defaults.hoursAgoText : _ref$hoursAgoText,
    _ref$illustrationThem = _ref.illustrationTheme,
    illustrationTheme = _ref$illustrationThem === void 0 ? defaults.illustrationTheme : _ref$illustrationThem,
    _ref$minuteAgoText = _ref.minuteAgoText,
    minuteAgoText = _ref$minuteAgoText === void 0 ? defaults.minuteAgoText : _ref$minuteAgoText,
    _ref$minutesAgoText = _ref.minutesAgoText,
    minutesAgoText = _ref$minutesAgoText === void 0 ? defaults.minutesAgoText : _ref$minutesAgoText,
    _ref$monthAgoText = _ref.monthAgoText,
    monthAgoText = _ref$monthAgoText === void 0 ? defaults.monthAgoText : _ref$monthAgoText,
    _ref$monthsAgoText = _ref.monthsAgoText,
    monthsAgoText = _ref$monthsAgoText === void 0 ? defaults.monthsAgoText : _ref$monthsAgoText,
    _ref$nowText = _ref.nowText,
    nowText = _ref$nowText === void 0 ? defaults.nowText : _ref$nowText,
    onClickOutside = _ref.onClickOutside,
    _ref$onDismissAllNoti = _ref.onDismissAllNotifications,
    onDismissAllNotifications = _ref$onDismissAllNoti === void 0 ? defaults.onDismissAllNotifications : _ref$onDismissAllNoti,
    _ref$onDismissSingleN = _ref.onDismissSingleNotification,
    onDismissSingleNotification = _ref$onDismissSingleN === void 0 ? defaults.onDismissSingleNotification : _ref$onDismissSingleN,
    onDoNotDisturbChange = _ref.onDoNotDisturbChange,
    onSettingsClick = _ref.onSettingsClick,
    onViewAllClick = _ref.onViewAllClick,
    open = _ref.open,
    _ref$previousLabel = _ref.previousLabel,
    previousLabel = _ref$previousLabel === void 0 ? defaults.previousLabel : _ref$previousLabel,
    _ref$readLessLabel = _ref.readLessLabel,
    readLessLabel = _ref$readLessLabel === void 0 ? defaults.readLessLabel : _ref$readLessLabel,
    _ref$readMoreLabel = _ref.readMoreLabel,
    readMoreLabel = _ref$readMoreLabel === void 0 ? defaults.readMoreLabel : _ref$readMoreLabel,
    _ref$secondsAgoText = _ref.secondsAgoText,
    secondsAgoText = _ref$secondsAgoText === void 0 ? defaults.secondsAgoText : _ref$secondsAgoText,
    _ref$settingsIconDesc = _ref.settingsIconDescription,
    settingsIconDescription = _ref$settingsIconDesc === void 0 ? defaults.settingsIconDescription : _ref$settingsIconDesc,
    _ref$title = _ref.title,
    title = _ref$title === void 0 ? defaults.title : _ref$title,
    _ref$todayLabel = _ref.todayLabel,
    todayLabel = _ref$todayLabel === void 0 ? defaults.todayLabel : _ref$todayLabel,
    _ref$viewAllLabel = _ref.viewAllLabel,
    viewAllLabel = _ref$viewAllLabel === void 0 ? defaults.viewAllLabel : _ref$viewAllLabel,
    _ref$yearAgoText = _ref.yearAgoText,
    yearAgoText = _ref$yearAgoText === void 0 ? defaults.yearAgoText : _ref$yearAgoText,
    _ref$yearsAgoText = _ref.yearsAgoText,
    yearsAgoText = _ref$yearsAgoText === void 0 ? defaults.yearsAgoText : _ref$yearsAgoText,
    _ref$yesterdayAtText = _ref.yesterdayAtText,
    yesterdayAtText = _ref$yesterdayAtText === void 0 ? defaults.yesterdayAtText : _ref$yesterdayAtText,
    _ref$yesterdayLabel = _ref.yesterdayLabel,
    yesterdayLabel = _ref$yesterdayLabel === void 0 ? defaults.yesterdayLabel : _ref$yesterdayLabel,
    triggerButtonRef = _ref.triggerButtonRef,
    rest = _objectWithoutProperties(_ref, _excluded);
  var notificationPanelRef = useRef(null);
  var notificationPanelInnerRef = useRef(null);
  var startSentinel = useRef(null);
  var endSentinel = useRef(null);
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    allNotifications = _useState2[0],
    setAllNotifications = _useState2[1];
  var supportedLocale = getSupportedLocale(dateTimeLocale, DefaultLocale);
  var carbonPrefix = usePrefix();
  var reducedMotion = usePrefersReducedMotion();
  var exitAnimationName = reducedMotion ? 'notifications-panel-exit-reduced' : 'notifications-panel-fade-out';
  var _usePresence = usePresence(open, notificationPanelRef, exitAnimationName),
    shouldRender = _usePresence.shouldRender;
  useEffect(function () {
    // Set the notifications passed to the state within this component
    setAllNotifications(data);
  }, [data]);
  useClickOutside(ref || notificationPanelRef, function () {
    onClickOutside === null || onClickOutside === void 0 || onClickOutside();
    setTimeout(function () {
      var _triggerButtonRef$cur;
      triggerButtonRef === null || triggerButtonRef === void 0 || (_triggerButtonRef$cur = triggerButtonRef.current) === null || _triggerButtonRef$cur === void 0 || _triggerButtonRef$cur.focus();
    }, 0);
  });
  var handleKeydown = function handleKeydown(event) {
    event.stopPropagation();
    if (event.key === 'Escape') {
      onClickOutside === null || onClickOutside === void 0 || onClickOutside();
      setTimeout(function () {
        var _triggerButtonRef$cur2;
        triggerButtonRef === null || triggerButtonRef === void 0 || (_triggerButtonRef$cur2 = triggerButtonRef.current) === null || _triggerButtonRef$cur2 === void 0 || _triggerButtonRef$cur2.focus();
      }, 100);
    }
  };
  useEffect(function () {
    // initialize the notification panel to open
    if (open) {
      var observer = new MutationObserver(function () {
        if (notificationPanelRef.current) {
          var _querySelector;
          var parentElement = notificationPanelRef.current;
          parentElement === null || parentElement === void 0 || (_querySelector = parentElement.querySelector(".".concat(blockClass, "__dismiss-button"))) === null || _querySelector === void 0 || _querySelector.focus();
          observer.disconnect();
        }
      });
      if (notificationPanelRef.current) {
        var parentElement = notificationPanelRef.current;
        var button = parentElement === null || parentElement === void 0 ? void 0 : parentElement.querySelector(".".concat(blockClass, "__dismiss-button"));
        button === null || button === void 0 || button.focus();
      } else {
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      }
      return function () {
        return observer.disconnect();
      };
    }
  }, [open]);
  var sortChronologically = function sortChronologically(arr) {
    if (!arr || arr && !arr.length) {
      return;
    }
    return arr.sort(function (a, b) {
      return b.timestamp - a.timestamp;
    });
  };

  // Notifications should be grouped by "Today", "Yesterday", and "Previous", the variables
  // below filter the notifications based on those conditions and then render them in those groups
  var yesterdayDate = new Date();
  yesterdayDate = new Date(yesterdayDate.setDate(yesterdayDate.getDate() - 1));
  var dayBeforeYesterdayDate = new Date();
  dayBeforeYesterdayDate = new Date(dayBeforeYesterdayDate.setDate(dayBeforeYesterdayDate.getDate() - 2));
  var withinLastDayNotifications = allNotifications && allNotifications.length && allNotifications.filter(function (item) {
    return item.timestamp.getTime() >= yesterdayDate.getTime();
  });
  withinLastDayNotifications = sortChronologically(withinLastDayNotifications);
  var previousDayNotifications = allNotifications && allNotifications.length && allNotifications.filter(function (item) {
    return item.timestamp.getTime() < yesterdayDate.getTime() && item.timestamp.getTime() >= dayBeforeYesterdayDate.getTime();
  });
  previousDayNotifications = sortChronologically(previousDayNotifications);
  var previousNotifications = allNotifications && allNotifications.length && allNotifications.filter(function (item) {
    return item.timestamp.getTime() < dayBeforeYesterdayDate.getTime();
  });
  previousNotifications = sortChronologically(previousNotifications);
  var renderDescription = function renderDescription(id) {
    var notification = allNotifications && allNotifications.length && allNotifications.filter(function (item) {
      return item.id === id;
    })[0];
    var trimLength = 88;
    var description = notification === null || notification === void 0 ? void 0 : notification['description'];
    var descriptionClassName = cx(["".concat(blockClass, "__notification-description"), _defineProperty(_defineProperty({}, "".concat(blockClass, "__notification-long-description"), notification === null || notification === void 0 ? void 0 : notification['showAll']), "".concat(blockClass, "__notification-short-description"), !(notification !== null && notification !== void 0 && notification['showAll']))]);
    var showMoreButtonClassName = cx([_defineProperty(_defineProperty({}, "".concat(blockClass, "__notification-read-less-button"), notification === null || notification === void 0 ? void 0 : notification['showAll']), "".concat(blockClass, "__notification-read-more-button"), !(notification !== null && notification !== void 0 && notification['showAll']))]);
    return /*#__PURE__*/React__default.createElement("div", null, /*#__PURE__*/React__default.createElement("p", {
      className: descriptionClassName
    }, description), description.length > trimLength && /*#__PURE__*/React__default.createElement(Button, {
      kind: "ghost",
      size: "sm",
      renderIcon: function renderIcon(props) {
        return /*#__PURE__*/React__default.createElement(ChevronDown, _extends({
          size: 16
        }, props));
      },
      iconDescription: notification !== null && notification !== void 0 && notification['showAll'] ? readLessLabel : readMoreLabel,
      onClick: function onClick(event) {
        event.preventDefault();
        event.stopPropagation();
        var newData = allNotifications.map(function (item) {
          if (item.id === (notification === null || notification === void 0 ? void 0 : notification['id'])) {
            return Object.assign({}, item, {
              showAll: !(item !== null && item !== void 0 && item['showAll'])
            });
          }
          return item;
        });
        setAllNotifications(newData);
      },
      className: showMoreButtonClassName
    }, notification !== null && notification !== void 0 && notification['showAll'] ? readLessLabel : readMoreLabel));
  };
  var renderNotification = function renderNotification(group, notification, index) {
    var notificationClassName = cx(["".concat(blockClass, "__notification"), "".concat(blockClass, "__notification-").concat(group)]);
    var notificationHeaderClassName = cx(["".concat(blockClass, "__notification-title"), _defineProperty({}, "".concat(blockClass, "__notification-title-unread"), notification.unread)]);
    return /*#__PURE__*/React__default.createElement("div", {
      key: "".concat(notification.timestamp, "-").concat(notification.title, "-").concat(index),
      className: notificationClassName,
      role: "button",
      tabIndex: 0,
      onClick: function onClick() {
        return notification.onNotificationClick(notification);
      },
      onKeyDown: function onKeyDown(event) {
        if (event.target instanceof HTMLElement && event.target.classList.contains("".concat(blockClass, "__dismiss-single-button"))) {
          return;
        }
        if (event.which === 13) {
          notification.onNotificationClick(notification);
        }
      }
    }, notification.type === 'error' && /*#__PURE__*/React__default.createElement(ErrorFilled, {
      size: 16,
      className: cx(["".concat(blockClass, "__notification-status-icon"), "".concat(blockClass, "__notification-status-icon-error")])
    }), notification.type === 'success' && /*#__PURE__*/React__default.createElement(CheckmarkFilled, {
      size: 16,
      className: cx(["".concat(blockClass, "__notification-status-icon"), "".concat(blockClass, "__notification-status-icon-success")])
    }), notification.type === 'warning' && /*#__PURE__*/React__default.createElement(WarningAltFilled, {
      size: 16,
      className: cx(["".concat(blockClass, "__notification-status-icon"), "".concat(blockClass, "__notification-status-icon-warning")])
    }), notification.type === 'informational' && /*#__PURE__*/React__default.createElement(InformationSquareFilled, {
      size: 16,
      className: cx(["".concat(blockClass, "__notification-status-icon"), "".concat(blockClass, "__notification-status-icon-informational")])
    }), /*#__PURE__*/React__default.createElement("div", {
      className: "".concat(blockClass, "__notification-content")
    }, /*#__PURE__*/React__default.createElement("p", {
      className: "".concat(blockClass, "__notification-time-label")
    }, dateTimeLocale ? dateTimeFormat.relative.format(notification.timestamp, {
      locale: supportedLocale,
      style: dateTimeStyle
    }) : timeAgo({
      previousTime: notification.timestamp,
      secondsAgoText: secondsAgoText,
      minuteAgoText: minuteAgoText,
      minutesAgoText: minutesAgoText,
      hoursAgoText: hoursAgoText,
      hourAgoText: hourAgoText,
      daysAgoText: daysAgoText,
      yesterdayAtText: yesterdayAtText,
      monthsAgoText: monthsAgoText,
      monthAgoText: monthAgoText,
      yearsAgoText: yearsAgoText,
      yearAgoText: yearAgoText,
      nowText: nowText
    })), /*#__PURE__*/React__default.createElement("h6", {
      className: notificationHeaderClassName
    }, notification.title), notification.description && notification.description.length && renderDescription(notification.id), notification.link && notification.link.text && notification.link.url && /*#__PURE__*/React__default.createElement(Link, _extends({
      href: notification.link.url,
      className: "".concat(blockClass, "__notifications-link")
    }, prepareProps({}, notification.link, ['text', 'url'])), notification.link.text)), /*#__PURE__*/React__default.createElement(IconButton, {
      align: "left",
      kind: "ghost",
      size: "sm",
      label: dismissSingleNotificationIconDescription,
      className: "".concat(blockClass, "__dismiss-single-button"),
      onClick: function onClick(event) {
        return dismissSingleNotification(event, notification);
      }
    }, _Close || (_Close = /*#__PURE__*/React__default.createElement(Close, {
      size: 16
    }))));
  };
  var dismissSingleNotification = function dismissSingleNotification(event, notification) {
    event.preventDefault();
    event.stopPropagation();
    onDismissSingleNotification(notification);
  };
  var mainSectionClassName = cx(["".concat(blockClass, "__main-section"), _defineProperty({}, "".concat(blockClass, "__main-section-empty"), allNotifications && !allNotifications.length)]);
  return shouldRender ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: "".concat(carbonPrefix, "--visually-hidden"),
    ref: startSentinel
  }, "Focus sentinel start"), /*#__PURE__*/React__default.createElement("div", _extends({
    role: "dialog",
    "aria-label": "Notification Panel",
    onKeyDown: handleKeydown,
    tabIndex: 0
  }, rest, {
    id: blockClass,
    className: cx(blockClass, className, "".concat(blockClass, "__container"), _defineProperty(_defineProperty({}, "".concat(blockClass, "__entrance"), open), "".concat(blockClass, "__exit"), !open)),
    ref: ref || notificationPanelRef
  }, getDevtoolsProps(componentName)), /*#__PURE__*/React__default.createElement("div", {
    ref: notificationPanelInnerRef
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "".concat(blockClass, "__header-container")
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "".concat(blockClass, "__header-flex")
  }, /*#__PURE__*/React__default.createElement("h2", {
    className: "".concat(blockClass, "__header")
  }, title), /*#__PURE__*/React__default.createElement(Button, {
    size: "sm",
    kind: "ghost",
    className: "".concat(blockClass, "__dismiss-button"),
    onClick: onDismissAllNotifications
  }, dismissAllLabel)), onDoNotDisturbChange && /*#__PURE__*/React__default.createElement(Toggle, {
    size: "sm",
    className: "".concat(blockClass, "__do-not-disturb-toggle"),
    id: "".concat(blockClass, "__do-not-disturb-toggle-component"),
    labelA: doNotDisturbLabel,
    labelB: doNotDisturbLabel,
    onToggle: function onToggle(event) {
      return onDoNotDisturbChange(event);
    },
    defaultToggled: doNotDisturbDefaultToggled,
    "aria-label": doNotDisturbLabel,
    labelText: doNotDisturbLabel
  })), /*#__PURE__*/React__default.createElement("div", {
    className: mainSectionClassName
  }, withinLastDayNotifications && withinLastDayNotifications.length ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("h6", {
    className: "".concat(blockClass, "__time-section-label")
  }, todayLabel), withinLastDayNotifications.map(function (notification, index) {
    return renderNotification('today', notification, index);
  })) : null, previousDayNotifications && previousDayNotifications.length ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("h6", {
    className: "".concat(blockClass, "__time-section-label")
  }, yesterdayLabel), previousDayNotifications.map(function (notification, index) {
    return renderNotification('yesterday', notification, index);
  })) : null, previousNotifications && previousNotifications.length ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("h6", {
    className: "".concat(blockClass, "__time-section-label")
  }, previousLabel), previousNotifications.map(function (notification, index) {
    return renderNotification('previous', notification, index);
  })) : null, !allNotifications.length && /*#__PURE__*/React__default.createElement(NotificationsEmptyState, {
    illustrationTheme: illustrationTheme,
    title: "",
    subtitle: emptyStateLabel
  })), onViewAllClick && onSettingsClick && allNotifications && allNotifications.length > 0 && /*#__PURE__*/React__default.createElement("div", {
    className: "".concat(blockClass, "__bottom-actions")
  }, /*#__PURE__*/React__default.createElement(Button, {
    kind: "ghost",
    className: "".concat(blockClass, "__view-all-button"),
    onClick: onViewAllClick
  }, viewAllLabel(allNotifications.length)), /*#__PURE__*/React__default.createElement(Button, {
    kind: "ghost",
    size: "sm",
    className: "".concat(blockClass, "__settings-button"),
    renderIcon: function renderIcon(props) {
      return /*#__PURE__*/React__default.createElement(Settings, _extends({
        size: 16
      }, props));
    },
    iconDescription: settingsIconDescription,
    onClick: onSettingsClick,
    hasIconOnly: true,
    tooltipPosition: "left"
  })))), /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: "".concat(carbonPrefix, "--visually-hidden"),
    ref: endSentinel
  }, "Focus sentinel end")) : null;
});

// Return a placeholder if not released and not enabled by feature flag
NotificationsPanel = pkg.checkComponentEnabled(NotificationsPanel, componentName);

// The display name of the component, used by React. Note that displayName
// is used in preference to relying on function.name.
NotificationsPanel.displayName = componentName;

// The types and DocGen commentary for the component props,
// in alphabetical order (for consistency).
// See https://www.npmjs.com/package/prop-types#usage.
NotificationsPanel.propTypes = {
  /**
   * Provide an optional class to be applied to the containing node.
   */
  className: PropTypes.string,
  /**
   * Array of data for Notifications component to render
   */
  /**@ts-ignore*/
  data: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.oneOf(['error', 'warning', 'success', 'informational']),
    timestamp: PropTypes.instanceOf(Date),
    title: PropTypes.string,
    description: PropTypes.string,
    link: PropTypes.shape({
      url: PropTypes.string,
      text: PropTypes.string
    }),
    unread: PropTypes.bool,
    onNotificationClick: PropTypes.func
  })).isRequired,
  /**
   * The language for each notification's time stamp.
   */
  dateTimeLocale: PropTypes.string,
  /**
   * The date/time format for each notification's time stamp.
   *
   * E.g. `long` as "6 minutes ago", `short` as "6m ago".
   */
  dateTimeStyle: PropTypes.string,
  /**
   * Sets the `days ago` label text
   */
  daysAgoText: PropTypes.func,
  /**
   * Label for Dismiss all button
   */
  dismissAllLabel: PropTypes.string,
  /**
   * Label for Dismiss single notification icon button
   */
  dismissSingleNotificationIconDescription: PropTypes.string,
  /**
   * Optional: Determines if the `Do not disturb` toggle is on or off when the component is rendered
   */
  doNotDisturbDefaultToggled: PropTypes.bool,
  /**
   * Optional: Label for Do not disturb toggle
   */
  doNotDisturbLabel: PropTypes.string,
  /**
   * Sets the empty state label text when there are no notifications
   */
  emptyStateLabel: PropTypes.string,
  /**
   * Sets the `hour ago` label text
   */
  hourAgoText: PropTypes.func,
  /**
   * Sets the `hours ago` label text
   */
  hoursAgoText: PropTypes.func,
  /**
   * Determines the theme of the empty state's illustration.
   */
  illustrationTheme: PropTypes.oneOf(['light', 'dark']),
  /**
   * Sets the `minute ago` label text
   */
  minuteAgoText: PropTypes.func,
  /**
   * Sets the `minutes ago` label text
   */
  minutesAgoText: PropTypes.func,
  /**
   * Sets the `month ago` label text
   */
  monthAgoText: PropTypes.func,
  /**
   * Sets the `months ago` label text
   */
  monthsAgoText: PropTypes.func,
  /**
   * Sets the `now` label text
   */
  nowText: PropTypes.string,
  /**
   * Optional function called after clicking outside of the panel.
   */
  onClickOutside: PropTypes.func,
  /**
   * Optional function called after clicking the "Dismiss all" button.
   */
  onDismissAllNotifications: PropTypes.func,
  /**
   * Optional function called after clicking a notification's "X" button.
   */
  onDismissSingleNotification: PropTypes.func,
  /**
   * Optional function called after toggling "Do not disturb".
   */
  onDoNotDisturbChange: PropTypes.func,
  /**
   * Optional function called after clicking settings / gear icon button.
   */
  onSettingsClick: PropTypes.func,
  /**
   * Optional function called after clicking the "View all" button.
   */
  onViewAllClick: PropTypes.func,
  /**
   * Determines whether the notifications panel should render or not
   */
  open: PropTypes.bool.isRequired,
  /**
   * Sets the previous label text
   */
  previousLabel: PropTypes.string,
  /**
   * Sets the `read less` label text
   */
  readLessLabel: PropTypes.string,
  /**
   * Sets the `read more` label text
   */
  readMoreLabel: PropTypes.string,
  /**
   * Sets the `seconds ago` label text
   */
  secondsAgoText: PropTypes.func,
  /**
   * Sets the settings icon description text
   */
  settingsIconDescription: PropTypes.string,
  /**
   * Sets the title for the Notifications panel
   */
  title: PropTypes.string,
  /**
   * Sets the today label text
   */
  todayLabel: PropTypes.string,
  /**
   * Sets the today label text
   */
  triggerButtonRef: PropTypes.any,
  /**
   * Sets the View all button text
   */
  viewAllLabel: PropTypes.func,
  /**
   * Sets the `year ago` label text
   */
  yearAgoText: PropTypes.func,
  /**
   * Sets the `years ago` label text
   */
  yearsAgoText: PropTypes.func,
  /**
   * Sets the `Yesterday at` label text
   */
  yesterdayAtText: PropTypes.func,
  /**
   * Sets the yesterday label text
   */
  yesterdayLabel: PropTypes.string
};

export { NotificationsPanel };
